import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { AlertTriangle, BarChart3, LineChart, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { kpis } from "../mocks";
import { JourneyFilter } from "./journey-filter";
import { KpiCards } from "./kpi-card";
import { LeadsTable } from "./leads-table";
import RankingParent from "./ranking-parent";
import { UsersTable } from "./users-table";
import LeadCostsTile from "./lead-costs-tile";
import RaceList from "./race-list";
import FunnelVisualization from "./funnel-visualization";
import PLZDistribution from "./plz-distribution";

export default function DashboardView() {
  const [journeyFilter, setJourneyFilter] = useState<string>("all");
  const [userRole, setUserRole] = useState<"admin" | "user">("admin"); // Default to admin for demo
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [range, setRange] = useState<"30d" | "mtd" | "qtd" | "ytd">("30d");

  useEffect(() => {
    const userId = searchParams.get("selectedUser");
    setSelectedUser(userId);
  }, [searchParams]);

  const handleUserSelect = (userId: string) => {
    setSearchParams({ selectedUser: userId });
  };

  const clearSelectedUser = () => {
    setSearchParams({});
    setSelectedUser(null);
  };

  const toggleRole = () => {
    setUserRole(userRole === "admin" ? "user" : "admin");
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight">
            {userRole === "admin" ? "Dashboard" : "Dashboard"}
            {selectedUser && (
              <span className="ml-4 text-lg font-normal text-muted-foreground">
                Benutzer anzeigen: {selectedUser}
                <button onClick={clearSelectedUser} className="ml-2 p-1 hover:bg-muted rounded-full">
                  <X className="h-4 w-4" />
                </button>
              </span>
            )}
          </h1>
          <div className="flex items-center gap-2">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as any)}
              className="text-xs bg-muted px-2 py-1 rounded-md"
              aria-label="Zeitraum"
            >
              <option value="30d">Letzte 30 Tage</option>
              <option value="mtd">Dieser Monat</option>
              <option value="qtd">Dieses Quartal</option>
              <option value="ytd">YTD</option>
            </select>
            {/* Ansicht umschalten (Admin/User) */}
            <button onClick={toggleRole} className="text-xs bg-muted px-2 py-1 rounded-md">
              Andere Ansicht
            </button>
          </div>
        </div>
        <p className="text-muted-foreground">
          Alle Daten auf einen Blick
        </p>
      </div>

      {userRole === "admin" && !selectedUser ? (
        <>
          {/* Org-View: Rangliste */}
          <RankingParent />

          <Card>
            <CardHeader>
              <CardTitle>Benutzer</CardTitle>
              <CardDescription>Alle Vertriebspartner im Überblick</CardDescription>
            </CardHeader>
            <CardContent>
              <UsersTable onUserSelect={handleUserSelect} />
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* KPIs */}
          <KpiCards kpis={kpis} />

          {/* Lead Costs */}
          <LeadCostsTile />

          {/* Enhanced Manager Dashboard Components */}
          <div className="grid gap-6 lg:grid-cols-2">
            <RaceList />
            <FunnelVisualization />
          </div>

          {/* PLZ Distribution */}
          <PLZDistribution />

          {/* Original Funnel + Trend */}
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader className="flex items-center justify-between">
                <div>
                  <CardTitle>Pipeline Details</CardTitle>
                  <CardDescription>Detaillierte Ansicht nach Stage</CardDescription>
                </div>
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <FunnelPreview />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Verkaufsleistung</CardTitle>
                <CardDescription>KW / Monat / Gesamt</CardDescription>
              </CardHeader>
              <CardContent className="h-[300px] flex items-center justify-center">
                <div className="text-muted-foreground flex flex-col items-center">
                  <LineChart className="h-16 w-16 mb-2" />
                  <p>Leistungsdiagramm wird hier angezeigt</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts + Leads */}
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Lead-Verwaltung</CardTitle>
                  <CardDescription>Verfolgen und verwalten Sie Ihre Leads</CardDescription>
                </div>
                <JourneyFilter value={journeyFilter} onValueChange={setJourneyFilter} />
              </CardHeader>
              <CardContent>
                <LeadsTable filter={journeyFilter} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex items-center justify-between">
                <div>
                  <CardTitle>Alerts</CardTitle>
                  <CardDescription>Überfällige Rechnungen & Webhooks</CardDescription>
                </div>
                <AlertTriangle className="h-5 w-5 text-orange-500" />
              </CardHeader>
              <CardContent>
                <AlertsPreview />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function FunnelPreview() {
  // Simple static preview bars; replace with API-driven data later
  const stages = [
    { name: "Qualifizierung", value: 18, color: "bg-blue-600" },
    { name: "Angebot", value: 12, color: "bg-blue-500" },
    { name: "Verhandlung", value: 7, color: "bg-blue-400" },
    { name: "Closing", value: 3, color: "bg-blue-300" },
  ];
  const max = Math.max(...stages.map((s) => s.value));
  return (
    <div className="space-y-3">
      {stages.map((s) => (
        <div key={s.name} className="w-full">
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
            <span>{s.name}</span>
            <span>{s.value}</span>
          </div>
          <div className="h-3 w-full rounded bg-muted">
            <div className={`h-3 rounded ${s.color}`} style={{ width: `${(s.value / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function AlertsPreview() {
  const items = [
    { type: "invoice", text: "3 Rechnungen überfällig (7.120 €)", tone: "text-red-600" },
    { type: "webhook", text: "2 Webhook-Fehler in den letzten 24h", tone: "text-orange-600" },
  ];
  return (
    <ul className="space-y-2 text-sm">
      {items.map((i, idx) => (
        <li key={idx} className={`flex items-center gap-2 ${i.tone}`}>
          <span className="inline-block h-2 w-2 rounded-full bg-current" />
          <span className="text-foreground">{i.text}</span>
        </li>
      ))}
      <li className="mt-3 text-muted-foreground">Details folgen nach Datenanbindung.</li>
    </ul>
  );
}
